/**
 * Splitting the free-text allergy field into one entry per allergen.
 *
 * The field is whatever reception typed at registration, and the shape
 * they actually type is
 *
 *   `Shellfish (Generalised urticaria, moderate), Sulfa drugs (rash, mild)`
 *
 * — a comma-separated list whose entries contain commas. Splitting on
 * every comma turns two allergens into four, of which two are the
 * fragments `moderate)` and `mild)`. Those fragments then render on the
 * chart's allergy panel as allergens in their own right, and the header's
 * "N Allergies" chip counts them: a patient with two allergies reads as
 * four, two of them nonsense.
 *
 * That is the one list on the chart where a wrong entry is a safety
 * problem, so the split has to respect the brackets the reaction and
 * severity live inside.
 */

/** Values that mean "nothing recorded" rather than an allergen. */
const NONE_RE =
  /^\s*(none|nkda|nka|no known allergies|no known drug allergies|n\/a|na|-|—|لا يوجد|لا شيء)\s*$/i;

/**
 * Fragments that are never an allergen on their own. These are what a
 * naive comma split leaves behind when it cuts through a parenthetical —
 * kept as a backstop for text whose brackets are unbalanced, which the
 * bracket-aware scanner below cannot rescue.
 */
const NOT_AN_ALLERGEN_RE =
  /^[\s)\]]*(mild|moderate|severe|life[-\s]?threatening|unknown|anaphylaxis|rash|itching|pruritus|urticaria|hives|swelling|nausea|vomiting|diarrhoea|diarrhea|خفيف|متوسط|شديد)[\s)\]]*$/i;

export function isNoAllergyText(value: string | null | undefined): boolean {
  const v = (value ?? '').trim();
  return !v || NONE_RE.test(v);
}

/**
 * Split on the separators reception types — but only at bracket depth
 * zero, so a comma inside `(...)` stays with its allergen.
 *
 * Newlines and semicolons split regardless of depth: they mean the typist
 * moved on to the next allergen, and treating an unclosed bracket as
 * swallowing every line after it is worse than splitting one line early.
 */
export function splitAllergyText(raw: string | null | undefined): string[] {
  if (isNoAllergyText(raw)) return [];

  const text = raw ?? '';

  // Bracket tracking is only safe when the brackets balance. An unclosed
  // `(` would otherwise swallow every allergen after it — in
  // `Aspirin (rash, severe, Latex` the patient's latex allergy would
  // vanish into the aspirin entry and never render. Over-splitting shows
  // a junk row the doctor can see and the backstop mostly catches;
  // under-splitting hides an allergy. So when the brackets don't balance,
  // fall back to splitting on every separator.
  const trackBrackets = bracketsBalance(text);

  const parts: string[] = [];
  let current = '';
  let depth = 0;

  for (const ch of text) {
    if (ch === '(' || ch === '[') {
      depth++;
      current += ch;
      continue;
    }
    if (ch === ')' || ch === ']') {
      // Clamp at zero so a stray closing bracket can't drive the depth
      // negative and make every later comma look nested.
      depth = Math.max(0, depth - 1);
      current += ch;
      continue;
    }
    const atTopLevel = !trackBrackets || depth === 0;
    const splitsHere =
      ch === '\n' || ch === ';' || (atTopLevel && (ch === ',' || ch === '/'));
    if (splitsHere) {
      parts.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  parts.push(current);

  const seen = new Set<string>();
  return parts
    .map((p) => p.trim())
    .filter((p) => {
      if (!p || NONE_RE.test(p) || NOT_AN_ALLERGEN_RE.test(p)) return false;
      const key = p.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

/** Do `(` / `[` all have a partner, in order? */
function bracketsBalance(text: string): boolean {
  let depth = 0;
  for (const ch of text) {
    if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') {
      depth--;
      if (depth < 0) return false;
    }
  }
  return depth === 0;
}

/**
 * Just the allergen, with the parenthetical reaction/severity stripped —
 * for matching against the structured `Allergy[]`, where the allergen is
 * stored on its own.
 */
export function allergenOf(entry: string): string {
  return entry
    .replace(/[([][^)\]]*[)\]]?/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** True for the severities that should not be read at a glance as routine. */
export function isSevereAllergy(entry: string): boolean {
  return /life[-\s]?threatening|anaphylaxis|severe/i.test(entry);
}
